import React, { useEffect, useRef, useState } from 'react';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import AOS from 'aos';
import {
  closeRegistrationModal,
  updateSalesCenterContactContext,
  useUiValue,
} from 'stores/uiContext';
import styled from 'styled-components';
import breakpoints from 'utils/breakpoints';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';

import loadable from '@loadable/component';
const FormsModal = loadable(() =>
  import('components/components/organisms/FormsModal'),
);

const Overlay = styled.div`
  background: #00000073;
  z-index: ${({ showSuccessModal }) => (!showSuccessModal ? '93' : '91')};
`;

const FlayoutContainer = styled.div`
  left: 0;
  right: 0;
  width: 100%;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: ${({ showSuccessModal }) => (!showSuccessModal ? '94' : '92')};
  transition: opacity 0.5s ease-out, transform 0.5s ease-out;

  &[data-aos='fade-left'] {
    transform: translate3d(100%, 0, 0);
  }

  &[data-aos='fade-left'].aos-animate {
    transform: translate3d(0, 0, 0);
  }

  &.is-closing {
    opacity: 0;
    transform: translate3d(100%, 0, 0) !important;
  }

  @media ${breakpoints.md} {
    left: auto;
    right: 0;
    max-width: 30rem;
  }

  @media ${breakpoints.lg} {
    top: ${({ top }) => top}px;
    height: calc(100vh - ${({ top }) => top}px);
  }
`;

const RegistrationModal = ({ fields, rendering: { placeholders } }) => {
  const [{ showRegistrationModal, showSuccessModal }, dispatch] = useUiValue();
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const touchStartYRef = useRef(0);
  const saleCenter = getFieldValue(fields, 'saleCenter') || {};

  const handleClose = () => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      dispatch(closeRegistrationModal());
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, 500);
  };

  useOnClickOutside(modalRef, () => {
    if (!showSuccessModal) {
      handleClose();
    }
  });

  useEffect(() => {
    dispatch(updateSalesCenterContactContext(saleCenter));
  }, [dispatch, saleCenter]);

  useEffect(() => {
    if (showRegistrationModal) {
      setIsClosing(false);
      setTimeout(() => AOS.refresh?.(), 0);
    }
  }, [showRegistrationModal]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    if (!showRegistrationModal) {
      return undefined;
    }

    const getScrollableElement = (target) => {
      let element = target;

      while (element && element !== modalRef.current) {
        const style = window.getComputedStyle(element);
        const canScroll =
          /(auto|scroll)/.test(style.overflowY) &&
          element.scrollHeight > element.clientHeight;

        if (canScroll) {
          return element;
        }

        element = element.parentElement;
      }

      return modalRef.current;
    };

    const shouldPreventModalScroll = (target, deltaY) => {
      const scrollableElement = getScrollableElement(target);

      if (!scrollableElement) {
        return true;
      }

      const isScrollingUp = deltaY < 0;
      const isScrollingDown = deltaY > 0;
      const isAtTop = scrollableElement.scrollTop <= 0;
      const isAtBottom =
        scrollableElement.scrollTop + scrollableElement.clientHeight >=
        scrollableElement.scrollHeight - 1;

      return (isScrollingUp && isAtTop) || (isScrollingDown && isAtBottom);
    };

    const handleWheel = (event) => {
      const modal = modalRef.current;

      if (!modal || !modal.contains(event.target)) {
        event.preventDefault();
        return;
      }

      if (shouldPreventModalScroll(event.target, event.deltaY)) {
        event.preventDefault();
      }
    };

    const handleTouchStart = (event) => {
      touchStartYRef.current = event.touches[0]?.clientY || 0;
    };

    const handleTouchMove = (event) => {
      const modal = modalRef.current;

      if (!modal || !modal.contains(event.target)) {
        event.preventDefault();
        return;
      }

      const currentY = event.touches[0]?.clientY || 0;
      const deltaY = touchStartYRef.current - currentY;

      if (shouldPreventModalScroll(event.target, deltaY)) {
        event.preventDefault();
      }
    };

    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [showRegistrationModal]);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  if (!showRegistrationModal) {
    return null;
  }

  return (
    <>
      <Overlay className='fixed inset-0' showSuccessModal={showSuccessModal} />
      <FlayoutContainer
        className={`fixed h-screen top-0 overflow-y-auto bg-white ${
          isClosing ? 'is-closing' : ''
        }`}
        showSuccessModal={showSuccessModal}
        data-aos='fade-left'
        data-aos-duration='500'
        data-aos-once='true'
        ref={modalRef}
      >
        <FormsModal
          fields={fields}
          placeholders={placeholders}
          onClose={handleClose}
        />
      </FlayoutContainer>
    </>
  );
};

export default RegistrationModal;
